import React from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { Box, Button, ButtonGroup, DropdownMenu, Flex, Menu, MenuItem, Switch, Text, Tooltip } from 'core/components';
import { TbChevronLeft, TbChevronRight, TbClockHour8, TbRefresh, TbRotateClockwise } from 'react-icons/tb';
import { Position } from '@blueprintjs/core';
import { UdeFormProps } from 'app/views/boards/panels/ude/form.types';
import BoardPanelModel from 'app/stores/boards/BoardPanelModel';

/**
 * Time range options for the date picker
 * value is in seconds
 */
const TIME_RANGE_OPTIONS = [
  { label: 'Last 5 minutes', value: 5 * 60 },
  { label: 'Last 15 minutes', value: 15 * 60 },
  { label: 'Last 1 hour', value: 60 * 60 },
  { label: 'Last 3 hours', value: 3 * 60 * 60 },
  { label: 'Last 6 hours', value: 6 * 60 * 60 },
  { label: 'Last 1 day', value: 24 * 60 * 60 },
  { label: 'Last 2 days', value: 2 * 24 * 60 * 60 },
  { label: 'Last 3 days', value: 3 * 24 * 60 * 60 },
  { label: 'Last 1 week', value: 7 * 24 * 60 * 60 }
] as const;

/**
 * Auto refresh interval options
 * value is in seconds
 */
const AUTO_REFRESH_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '5s', value: 5 },
  { label: '15s', value: 15 },
  { label: '30s', value: 30 },
  { label: '1m', value: 60 },
  { label: '5m', value: 5 * 60 },
  { label: '30m', value: 30 * 60 }
] as const;

/**
 * Format a lookback value in seconds to a human-readable string
 */
function formatLookback(seconds: number): string {
  const humanized = moment.duration(seconds, 'seconds').humanize();
  // moment humanize uses "a/an" for singular units - replace with "1"
  const normalized = humanized.replace(/^an? /, '1 ');
  return `Last ${normalized}`;
}

interface UdePanelToolsProps extends UdeFormProps {
  panel: BoardPanelModel;
}

/**
 * State for tracking the current time window
 */
interface TimeWindowState {
  /** Duration of the window in seconds */
  duration: number;
  /** Start timestamp in epoch seconds (only set when sliding) */
  start: number | null;
  /** End timestamp in epoch seconds (only set when sliding) */
  end: number | null;
}

const UdePanelTools = observer(({ panel, form }: UdePanelToolsProps) => {
  // const [enableForecasting, setEnableForecasting] = React.useState(false);
  // const [enableBaselines, setEnableBaselines] = React.useState(false);

  // Initialize from form value if it exists
  const [enablePeriodOverPeriod, setEnablePeriodOverPeriod] = React.useState(false);
  const [enableAwsTimestamps, setEnableAwsTimestamps] = React.useState(false);

  // Track the current time window for sliding
  const [timeWindow, setTimeWindow] = React.useState<TimeWindowState>({
    duration: form.getValue('config.data.time.lookback') || 3600,
    start: form.getValue('config.data.time.start') || null,
    end: form.getValue('config.data.time.end') || null
  });

  const [enableAutoRefresh, setEnableAutoRefresh] = React.useState<number>(
    form?.getValue('config.data.updateFrequency')
  );

  // Check if we're in sliding mode (using absolute times vs relative lookback)
  const isSliding = timeWindow.start !== null && timeWindow.end !== null;

  const updateFormAndModel = () => {
    if (form) {
      const formConfig = form.getValues().config;
      panel.set({ config: formConfig });
    }
  };

  const handleRefresh = () => {
    if (isSliding && timeWindow.start !== null && timeWindow.end !== null) {
      // In sliding mode, re-run with the current absolute window
      panel.queryModel?.run({ time: { lookback: 0, start: timeWindow.start, end: timeWindow.end } });
    } else {
      // In relative mode, use the current form time value
      const currentLookback = form?.getValue('config.data.time.lookback');
      if (currentLookback) {
        panel.queryModel?.run({ time: { lookback: currentLookback, start: 0, end: 0 } });
      } else {
        panel.queryModel?.run();
      }
    }
  };

  // pass in empty or 0 to reset current time now.
  const handleTimeRangeChange = (lookbackSeconds?: number) => {
    // Reset to relative mode when selecting a preset
    setTimeWindow({
      duration: lookbackSeconds || timeWindow.duration,
      start: null,
      end: null
    });

    // Update the form value - this keeps the form in sync for saving
    form.setValue('config.data.time.lookback', lookbackSeconds || timeWindow.duration);
    form.setValue('config.data.time.start', 0);
    form.setValue('config.data.time.end', 0);
    updateFormAndModel();
  };

  // const handleResetToNow = () => {
  //   // Reset to relative mode
  //   setTimeWindow({
  //     duration: timeWindow.duration,
  //     start: null,
  //     end: null
  //   });

  //   form.setValue('config.data.time.lookback', timeWindow.duration);
  //   form.setValue('config.data.time.start', 0);
  //   form.setValue('config.data.time.end', 0);
  //   updateFormAndModel();
  // };

  const handleSlideBack = () => {
    const now = Math.floor(Date.now() / 1000);
    const { duration } = timeWindow;

    let newEnd: number;
    let newStart: number;

    if (isSliding && timeWindow.start !== null) {
      // Already sliding - move the window back by its duration
      newEnd = timeWindow.start;
      newStart = newEnd - duration;
    } else {
      // First slide - convert from relative to absolute
      newEnd = now - duration;
      newStart = newEnd - duration;
    }

    setTimeWindow({
      duration,
      start: newStart,
      end: newEnd
    });

    form.setValue('config.data.time.lookback', 0);
    form.setValue('config.data.time.start', newStart);
    form.setValue('config.data.time.end', newEnd);
    updateFormAndModel();
  };

  const handleSlideForward = () => {
    const now = Math.floor(Date.now() / 1000);
    const { duration } = timeWindow;

    if (!isSliding || timeWindow.end === null) {
      // Not in sliding mode, nothing to do
      return;
    }

    const newStart = timeWindow.end;
    const newEnd = newStart + duration;

    // Don't allow sliding into the future
    if (newEnd >= now) {
      // Reset to relative mode (back to "now")
      handleTimeRangeChange();
      return;
    }

    setTimeWindow({
      duration,
      start: newStart,
      end: newEnd
    });

    form.setValue('config.data.time.lookback', 0);
    form.setValue('config.data.time.start', newStart);
    form.setValue('config.data.time.end', newEnd);
    updateFormAndModel();
  };

  const handleAutoRefreshChange = (seconds: number) => {
    setEnableAutoRefresh(seconds);
    // Update the form value - this will be included in the next query
    form.setValue('config.data.updateFrequency', seconds);
    updateFormAndModel();
  };

  /**
   * Get the display label for the current auto-refresh interval
   */
  const getAutoRefreshDisplay = () => {
    if (enableAutoRefresh === 0) {
      return 'Off';
    }
    if (enableAutoRefresh < 60) {
      return `${enableAutoRefresh}s`;
    }
    if (enableAutoRefresh < 3600) {
      return `${enableAutoRefresh / 60}m`;
    }
    return `${enableAutoRefresh / 3600}h`;
  };

  /**
   * Format the time display based on current mode
   */
  const getTimeDisplay = () => {
    if (isSliding && timeWindow.start !== null && timeWindow.end !== null) {
      const startMoment = moment.unix(timeWindow.start).utc();
      const endMoment = moment.unix(timeWindow.end).utc();
      // Show date range for sliding window
      return `${startMoment.format('MMM D HH:mm')} - ${endMoment.format('HH:mm')} UTC`;
    }
    return formatLookback(timeWindow.duration);
  };

  return (
    <Flex alignItems="center" pr={1} gap={1}>
      {/* <DropdownMenu position={Position.BOTTOM_LEFT}>
        <Button
          small
          minimal
          text={
            <Flex alignItems="center" gap="4px">
              <Text>Baselines</Text>
              {enableBaselines && (
                <Tag small round intent="success" minimal>
                  On
                </Tag>
              )}
            </Flex>
          }
          icon={TbChartArea}
          rightIcon="caret-down"
        />
        <Box p={1} minWidth={160}>
          <Switch
            checked={enableBaselines}
            labelElement={<Text small>Enabled</Text>}
            onChange={() => setEnableBaselines(!enableBaselines)}
          />
        </Box>
      </DropdownMenu> */}

      {/* <DropdownMenu position={Position.BOTTOM_LEFT}>
        <Button
          small
          minimal
          text={
            <Flex alignItems="center" gap="4px">
              <Text>Forecasting</Text>
              {enableForecasting && (
                <Tag small round intent="success" minimal>
                  On
                </Tag>
              )}
            </Flex>
          }
          icon={TbChartCovariate}
          rightIcon="caret-down"
        />
        <Box p={1} minWidth={160}>
          <Switch
            checked={enableForecasting}
            labelElement={<Text small>Enabled</Text>}
            onChange={() => setEnableForecasting(!enableForecasting)}
          />
        </Box>
      </DropdownMenu> */}

      {/* Time Window Navigation */}
      <ButtonGroup>
        <Tooltip content="Slide window back" position={Position.BOTTOM}>
          <Button minimal small icon={TbChevronLeft} onClick={handleSlideBack} disabled={panel.queryModel?.isLoading} />
        </Tooltip>

        <DropdownMenu position={Position.BOTTOM_LEFT}>
          <Button
            small
            minimal
            icon={TbClockHour8}
            rightIcon="caret-down"
            text={
              <Text fontWeight="medium">
                {getTimeDisplay()} {!isSliding && <Text muted>UTC</Text>}
              </Text>
            }
          />
          <Box minWidth={200}>
            <Menu>
              {TIME_RANGE_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  text={option.label}
                  active={!isSliding && form?.getValue('config.data.time.lookback') === option.value}
                  onClick={() => handleTimeRangeChange(option.value)}
                />
              ))}
            </Menu>
            <Box borderTop="1px solid" borderColor="divider" p={1}>
              <Switch
                label="Compare over previous period"
                checked={enablePeriodOverPeriod}
                onChange={() => setEnablePeriodOverPeriod(!enablePeriodOverPeriod)}
              />
              <Switch
                label="Use AWS Timestamps (Beta)"
                checked={enableAwsTimestamps}
                onChange={() => setEnableAwsTimestamps(!enableAwsTimestamps)}
              />
            </Box>
          </Box>
        </DropdownMenu>

        <Tooltip content="Slide window forward" position={Position.BOTTOM}>
          <Button
            minimal
            small
            icon={TbChevronRight}
            onClick={handleSlideForward}
            disabled={panel.queryModel?.isLoading || !isSliding}
          />
        </Tooltip>

        {isSliding && (
          <Tooltip content="Jump to now" position={Position.BOTTOM}>
            <Button
              minimal
              small
              icon={TbRotateClockwise}
              onClick={() => handleTimeRangeChange()}
              disabled={panel.queryModel?.isLoading}
            />
          </Tooltip>
        )}
      </ButtonGroup>

      <ButtonGroup>
        <Tooltip content="Refresh" position={Position.BOTTOM}>
          <Button minimal icon={TbRefresh} small onClick={handleRefresh} disabled={panel.queryModel?.isLoading} />
        </Tooltip>

        <DropdownMenu position={Position.BOTTOM_LEFT}>
          <Tooltip
            content={`Auto refresh ${enableAutoRefresh ? 'enabled' : 'disabled'}`}
            position={Position.BOTTOM_RIGHT}
          >
            <Button minimal small rightIcon="caret-down" text={getAutoRefreshDisplay()} />
          </Tooltip>
          <Box minWidth={160}>
            <Menu>
              {AUTO_REFRESH_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  text={option.label}
                  onClick={() => handleAutoRefreshChange(option.value)}
                />
              ))}
            </Menu>
          </Box>
        </DropdownMenu>
      </ButtonGroup>
    </Flex>
  );
});

export default UdePanelTools;
